import { useState } from 'react';
import { OnboardingTask } from '@tiendanube/common';
import { Country } from '@tiendanube/common/src/enums';
import { NUVEMPAGO_ENABLED, PROJECT_SHIVA_TAG } from 'App/featuresFlags';
import { useHasTags, useNavegate } from 'App/hooks';
import {
  trackingDashboardSeeStoreClick,
  trackingGoPosClick,
  trackingMeliSyncClick,
  trackingMeliSyncDismissClick,
  trackingMxPreferencesNubeClick,
  trackingPaymentOthersClick,
  trackingRegisterLocationClick,
  trackingSeePreferencesNuvemEnvioClick,
  trackingSeePreferencesNuvemPagoClick,
  trackingSeePreferencesPagoNubeClick,
  trackingThemePersonalizeClick,
} from 'App/tracking';
import { openWindow } from 'commons/utils/window';
import { useGetCountry, useLoggedAdminLink } from 'domains/Auth/hooks';
import dashboardService from 'domains/Dashboard/services';
import {
  trackingAddProductClick,
  trackingShippingClick,
} from 'domains/Dashboard/tracking';
import useOnboardingTasks from '../../useOnboardingTasks';

const PRINCIPAL_LOG_EVENTS = {
  store_created: trackingDashboardSeeStoreClick,
  setup_layout: trackingThemePersonalizeClick,
  setup_first_product: trackingAddProductClick,
  setup_multicd: trackingRegisterLocationClick,
  setup_meli_sync: trackingMeliSyncClick,
};

export const useOnboardingTasksConfig = (sortedItems: OnboardingTask[]) => {
  const { goTo } = useNavegate();
  const { getOnboarding } = useOnboardingTasks();
  const { goToLoggedAdminLink } = useLoggedAdminLink();
  const [isProjectShiva, isNuvemPagoMx] = useHasTags([
    PROJECT_SHIVA_TAG,
    NUVEMPAGO_ENABLED,
  ]);
  const storeCountry = useGetCountry();
  const [isLoadingPayments, setIsLoadingPayments] = useState(false);
  const [isLoadingShipping, setIsLoadingShipping] = useState(false);
  const [isLoadingMeliSync, setIsLoadingMeliSync] = useState(false);
  const [isLoadingTestingSale, setIsLoadingTestingSale] = useState(false);

  const taskConfig = (task: string, url = 'none') => {
    if (task === 'setup_meli_sync') {
      return goToConfig('none', task);
    }

    if (task === 'setup_shipping' || task === 'setup_payments') {
      return goToConfig(url, task, true);
    }
  };

  const goToConfig =
    (url: string, task: string, isSecondaryLink = false) =>
    async () => {
      switch (task) {
        case 'setup_payments':
          await paymentConfig(url, isSecondaryLink);
          break;
        case 'setup_shipping':
          await shippingConfig(url, task, isSecondaryLink);
          break;
        case 'setup_testing_sale':
          await posTestingSaleConfig(url);
          break;
        default:
          await meliSyncConfig(url);
          break;
      }
    };

  const posTestingSaleConfig = async (url: string) => {
    setIsLoadingTestingSale(true);
    try {
      await dashboardService.updateOnboardingTaskSetupTestingSale();
      getOnboarding();
      trackingGoPosClick();
      openWindow(url, true);
    } finally {
      setIsLoadingTestingSale(false);
    }
  };

  const meliSyncConfig = async (url: string) => {
    setIsLoadingMeliSync(true);
    try {
      await dashboardService.updateOnboardingTaskSetupMeliSync();
      getOnboarding();
      if (url !== 'none') {
        trackingMeliSyncClick();
        goTo(url);
      } else {
        trackingMeliSyncDismissClick();
      }
    } finally {
      setIsLoadingPayments(false);
    }
  };

  const paymentConfig = async (url: string, isSecondaryLink: boolean) => {
    setIsLoadingPayments(true);
    const isCountryNeeded = [Country.BR, Country.AR, Country.MX].includes(
      storeCountry,
    );
    const isMx = Country.MX === storeCountry;

    try {
      if (isProjectShiva && isSecondaryLink) {
        if (isCountryNeeded) {
          goTo(url);
          return;
        } else {
          setIsLoadingPayments(false);
          openWindow(url, true);
          return;
        }
      }

      if (isProjectShiva && url.startsWith('/settings/payments/nuvempago')) {
        trackingSeePreferencesNuvemPagoClick();
        goTo(url);
      }

      //Main link
      if (isMx && isNuvemPagoMx && !isSecondaryLink) {
        trackingMxPreferencesNubeClick();
        goTo(url);
        return;
      }

      if (url.startsWith('/settings/payments')) {
        trackingPaymentOthersClick();
        goTo(url);
      } else {
        if (!isProjectShiva) {
          await dashboardService.updateOnboardingTaskSetupPayments();
        }
        if (url.startsWith('/admin')) {
          trackingSeePreferencesNuvemPagoClick();
          url = url.replace('/admin', '');
          goToLoggedAdminLink(url);
        } else {
          trackingSeePreferencesPagoNubeClick();
          goTo(url);
        }
      }
    } finally {
      setIsLoadingPayments(false);
    }
  };

  const shippingConfig = async (
    url: string,
    task: string,
    isSecondaryLink: boolean,
  ) => {
    setIsLoadingShipping(true);
    try {
      if (isProjectShiva && isSecondaryLink) {
        if (storeCountry === Country.BR) {
          const isMultiCDCompleted = sortedItems.find(
            (item) => item.name === 'setup_multicd' && item.completed === true,
          );
          if (isMultiCDCompleted) {
            await dashboardService.updateOnboardingTaskSetupShippingNative();
          }
        } else {
          setIsLoadingShipping(false);
          openWindow(url, true);
          return;
        }
      } else if (url.startsWith('/settings/shipping-methods/carriers')) {
        await dashboardService.updateOnboardingTaskSetupShippingNative();
      }
      trackEvent(url, task)();
      goTo(url);
    } finally {
      setIsLoadingShipping(false);
    }
  };

  const trackEvent = (url: string, taskName: string) => () => {
    if (taskName === 'setup_shipping') {
      if (url.startsWith('/settings/shipping-methods/carriers')) {
        trackingSeePreferencesNuvemEnvioClick();
      } else {
        trackingShippingClick();
      }
    } else {
      PRINCIPAL_LOG_EVENTS[taskName]();
    }
  };

  const isTaskLoading = (taskName: string) => {
    switch (taskName) {
      case 'setup_payments':
        return isLoadingPayments;
      case 'setup_shipping':
        return isLoadingShipping;
      case 'setup_testing_sale':
        return isLoadingTestingSale;
      default:
        return isLoadingMeliSync;
    }
  };

  return {
    taskConfig,
    goToConfig,
    trackEvent,
    isTaskLoading,
    isLoadingPayments,
    isLoadingShipping,
    isLoadingMeliSync,
    isLoadingTestingSale,
  };
};
